import { has } from 'lodash'

type ParsedSearchPrefix<Category extends string> = {
  /** The category the prefix names, or null when the query carries no known prefix. */
  category: Category | null
  /** The query with any recognized prefix removed. */
  searchTerm: string
}

/**
 * Splits a `category:term` search query ("charger:ev100", "c:ev100") into the
 * category the prefix names and the term after it. The prefix is matched
 * case-insensitively against `prefixes`, and leading whitespace after the colon
 * is dropped. A query with no colon, or with a prefix `prefixes` doesn't list,
 * is returned whole as a plain term, so text that merely contains a colon still
 * searches normally.
 */
export default function parseSearchPrefix<Category extends string>(
  query: string,
  prefixes: Readonly<Record<string, Category>>
): ParsedSearchPrefix<Category> {
  const colonIndex = query.indexOf(':')
  if (colonIndex === -1) return { category: null, searchTerm: query }

  const prefix = query.slice(0, colonIndex).trim().toLowerCase()
  const category = has(prefixes, prefix) ? prefixes[prefix] : undefined
  if (!category) return { category: null, searchTerm: query }

  return { category, searchTerm: query.slice(colonIndex + 1).trimStart() }
}
